import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import ActionButton from "components/admin/ActionButton";
import Pagination from "../creditsManagement/pagination";
import GiveawayContestTable from "./GiveawayContestTable";
import { GiveawayContest } from "types/giveaway";
import { getGiveawayContests } from "apis/adminServices/giveawayServices";
import { useToastStore } from "store/toastStore";

type TabKey = "latest" | "previous";

const TAB_LABELS: Record<TabKey, string> = {
  latest: "Latest Giveaway Contest",
  previous: "Previous Giveaway Contest",
};

const TAB_SUBTITLES: Record<TabKey, string> = {
  latest: "Details of the latest Giveaway contest",
  previous: "Details of the previous Giveaway contest",
};

const GiveawayManagement: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialTab = (location.state as { tab?: TabKey } | null)?.tab ?? "latest";
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);

  // The backend returns the two lists already split by section: `latest` holds
  // the live and scheduled contests, `previous` the concluded ones. Both are
  // rendered as they arrive — the server's ordering included.
  const [current, setCurrent] = useState<GiveawayContest[]>([]);
  const [previous, setPrevious] = useState<GiveawayContest[]>([]);
  const [loading, setLoading] = useState(false);

  // Paging applies to the `previous` list, which is the one that grows.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // The backend returns every `latest` contest in one shot (it isn't paged
  // server-side), so its pager just slices the already-fetched array.
  const [latestPage, setLatestPage] = useState(1);
  const [latestPageSize, setLatestPageSize] = useState(10);

  const { showToast } = useToastStore();

  const fetchContests = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getGiveawayContests({ page, size: pageSize });
      setCurrent(response.latest ?? []);
      setPrevious(response.previous ?? []);
      setTotalCount(response.totalCount ?? 0);
      setLatestPage(1);
    } catch (error) {
      console.error("Failed to load giveaway contests:", error);
      setCurrent([]);
      setPrevious([]);
      setTotalCount(0);
      showToast("Failed to load giveaway contests", "error", 3000);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  useEffect(() => {
    fetchContests();
  }, [fetchContests]);

  // A bigger page can leave the current one past the end of the archive.
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  const handleLatestPageSizeChange = (size: number) => {
    setLatestPageSize(size);
    setLatestPage(1);
  };

  const pagedCurrent = useMemo(
    () => current.slice((latestPage - 1) * latestPageSize, latestPage * latestPageSize),
    [current, latestPage, latestPageSize],
  );

  const handleAdd = () => navigate("/admin/giveaway-management/new");

  // Details and edit share one screen — the form page loads the contest by id.
  const handleViewDetails = (contest: GiveawayContest) =>
    navigate(`/admin/giveaway-management/${contest.contestId}`);

  // A finished contest opens a different screen: same contest, plus the
  // published winners, minus the fields that only matter while it is running.
  const handleViewPastDetails = (contest: GiveawayContest) =>
    navigate(`/admin/giveaway-management/${contest.contestId}/details`);

  const handleViewWinners = (contest: GiveawayContest) =>
    navigate(`/admin/giveaway-management/${contest.contestId}/winners`);

  return (
    <>
      {/* Page heading */}
      <section className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-bold text-grease-100 tracking-tight">
          Giveaway Management
        </h1>

        <nav className="flex flex-wrap items-center text-sm text-lightgray-200 gap-x-2 gap-y-1 mt-1">
          <span className="st-dashboard text-lightgray-200 text-sm" aria-hidden="true" />
          <Link to="/admin/dashboard" className="hover:text-royalblue-100 transition-colors duration-150">
            Dashboard
          </Link>
          <span className="st-arrow-right text-lightgray-200 text-xs" aria-hidden="true" />
          <span className="text-royalblue-100 font-medium">Giveaway Management</span>
        </nav>
      </section>

      <div className="w-full overflow-x-auto no-scrollbar mt-3">
        <div className="flex items-center gap-2 p-2 bg-blue-100 rounded-full w-max mx-auto">
          {(["latest", "previous"] as const).map((tab) => (
            <button
              key={tab}
              className={`px-4 sm:px-6 py-2 rounded-full text-sm font-medium whitespace-nowrap shrink-0 ${
                activeTab === tab ? "bg-white text-grease-100" : "text-lightgray-200"
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {TAB_LABELS[tab]}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white p-4 md:p-6 rounded-xl mt-3">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4 w-full">
          <div className="break-words">
            <p className="text-base font-bold text-grease-100">{TAB_SUBTITLES[activeTab]}</p>
          </div>

          {activeTab === "latest" && (
            <div className="w-full md:w-auto shrink-0 flex items-center justify-start md:justify-end">
              <ActionButton
                onClick={handleAdd}
                icon={<Plus className="w-4 h-4" />}
                className="w-full md:w-auto"
              >
                Create Giveaway
              </ActionButton>
            </div>
          )}
        </div>

        {activeTab === "latest" ? (
          <GiveawayContestTable
            contests={pagedCurrent}
            loading={loading}
            emptyMessage="No giveaway contests yet"
            onViewDetails={handleViewDetails}
            onViewWinners={handleViewWinners}
          />
        ) : (
          <GiveawayContestTable
            contests={previous}
            loading={loading}
            emptyMessage="No previous giveaway contests to show."
            showReward
            onViewDetails={handleViewPastDetails}
          />
        )}

        {/* Latest paginates client-side over the already-fetched list; the
            archive (previous) paginates server-side. */}
        {activeTab === "latest" && current.length > 0 && (
          <Pagination
            currentPage={latestPage}
            totalItems={current.length}
            onPageChange={setLatestPage}
            pageSize={latestPageSize}
            onPageSizeChange={handleLatestPageSizeChange}
          />
        )}
        {activeTab === "previous" && totalCount > 0 && (
          <Pagination
            currentPage={page}
            totalItems={totalCount}
            onPageChange={setPage}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </div>
    </>
  );
};

export default GiveawayManagement;
